import axios from 'axios'
import toast from 'react-hot-toast'
import { API_URL } from '../config'

export const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
      return Promise.reject(error)
    }
    
    const message = error.response?.data?.error || error.message || 'An error occurred'
    toast.error(message)
    
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (data: { name: string; email: string; password: string }) =>
    api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  /** Public. Returns { open } so the UI can hide a form the backend would reject. */
  registrationStatus: () => api.get<{ open: boolean }>('/auth/registration-status'),
  updateProfile: (data: { name?: string; email?: string }) =>
    api.put('/auth/profile', data),
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.put('/auth/change-password', data),
  uploadProfileImage: (file: File) => {
    const formData = new FormData()
    formData.append('profileImage', file)
    return api.post('/auth/profile-image', formData)
  },
  removeProfileImage: () => api.delete('/auth/profile-image'),
  logout: () => api.post('/auth/logout'),
}

// Analytics API
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getConversations: (days = 30) => api.get(`/analytics/conversations?days=${days}`),
  getTopics: (days = 30) => api.get(`/analytics/topics?days=${days}`),
  getEngagement: (days = 30) => api.get(`/analytics/engagement?days=${days}`),
  getKnowledge: () => api.get('/analytics/knowledge'),
  getPerformance: (days = 7) => api.get(`/analytics/performance?days=${days}`),
  getActivity: (limit = 20) => api.get(`/analytics/activity?limit=${limit}`),
}

// Knowledge API
export const knowledgeAPI = {
  getAll: (params = {}) => api.get('/knowledge', { params }),
  getById: (id: string) => api.get(`/knowledge/${id}`),
  uploadFile: (file: File, title?: string, tags?: string[]) => {
    const formData = new FormData()
    formData.append('file', file)
    if (title) formData.append('title', title)
    if (tags) formData.append('tags', tags.join(','))
    return api.post('/knowledge/upload', formData)
  },
  addUrl: (data: { url: string; title?: string; tags?: string[] }) =>
    api.post('/knowledge/url', data),
  update: (id: string, data: any) => api.put(`/knowledge/${id}`, data),
  delete: (id: string) => api.delete(`/knowledge/${id}`),
  refresh: (id: string) => api.post(`/knowledge/${id}/refresh`),
  search: (query: string, limit = 5) => api.get(`/knowledge/search/${query}?limit=${limit}`),
  getStats: () => api.get('/knowledge/stats/overview'),
}

// Branding API
export const brandingAPI = {
  get: () => api.get('/branding'),
  update: (data: any) => api.post('/branding', data),
  updateById: (id: string, data: any) => api.put(`/branding/${id}`, data),
  uploadLogo: (file: File) => {
    const formData = new FormData()
    formData.append('logo', file)
    return api.post('/branding/logo', formData)
  },
  removeLogo: () => api.delete('/branding/logo'),
  getHistory: () => api.get('/branding/history'),
  activate: (id: string) => api.post(`/branding/${id}/activate`),
  preview: (data: any) => api.post('/branding/preview', data),
  reset: () => api.post('/branding/reset'),
}

// Inquiry API
export const inquiryAPI = {
  getAll: (params = {}) => api.get('/inquiries', { params }),
  getById: (id: string) => api.get(`/inquiries/${id}`),
  update: (id: string, data: any) => api.put(`/inquiries/${id}`, data),
  delete: (id: string) => api.delete(`/inquiries/${id}`),
  bulkUpdate: (ids: string[], updates: any) =>
    api.put('/inquiries/bulk/update', { ids, updates }),
  exportCSV: (params = {}) => api.get('/inquiries/export/csv', { 
    params, 
    responseType: 'blob' 
  }),
  getStats: () => api.get('/inquiries/stats/overview'),
  getTrends: (days = 30) => api.get(`/inquiries/stats/trends?days=${days}`),
  submit: (data: any) => api.post('/inquiries/submit', data),
}

// Conversation API (for chat widget)
export const chatAPI = {
  createSession: (metadata = {}) => api.post('/chat/session', { metadata }),
  sendMessage: (data: { message: string; sessionId: string; userId?: string }) =>
    api.post('/chat/message', data),
  getConversation: (sessionId: string) => api.get(`/chat/conversation/${sessionId}`),
  getBranding: () => api.get('/chat/branding'),
  endConversation: (sessionId: string) => api.post(`/chat/end/${sessionId}`),
}